import { useState } from "react";
import { fmtMoney, fmtPct } from "../parsers/xmlParser";
import { CATEGORY_STYLE } from "../utils/fieldMaps";
import { CategoryBadge, StatusBadge } from "./UI";
import ExpandedPanel from "./FundsTableExpanded";
import { useFundNotesStore, getFundUniqueKey } from "../hooks/useFundNotesStore";
import { useSnapshotStore } from "../hooks/useSnapshotStore";

export default function FundsMobileList({ rows, clientId }) {
  const [openId, setOpenId] = useState(null);
  const { notes, setNote } = useFundNotesStore();
  const { snapshot, updateFundComment } = useSnapshotStore(clientId);

  return (
    <div dir="rtl" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map(({ fund: f, computed: c }) => {
        const cs = CATEGORY_STYLE[f.cat] || CATEGORY_STYLE["ביטוח מנהלים"];
        const isOpen = openId === f.id;
        return (
          <div key={f.id} style={{
            background: isOpen ? cs.bg : "white",
            border: `1px solid ${isOpen ? cs.border : "#e2e8f0"}`,
            borderRight: `3px solid ${cs.border || cs.accent}`,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}>
            {/* Card header — tappable */}
            <div
              onClick={() => setOpenId(isOpen ? null : f.id)}
              style={{ padding: "12px 14px", cursor: "pointer" }}
            >
              {/* Row 1: category + company + product */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                <CategoryBadge cat={f.cat} />
                <span style={{ fontSize: 12, color: "#64748b" }}>{f.companyName}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>
                  {f.tochnit || f.mugName || "—"}
                </span>
              </div>
              {/* Row 2: polNum + status */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                {f.polNum && (
                  <span style={{ fontSize: 11, color: cs.accent }}>#{f.polNum}</span>
                )}
                <StatusBadge label={f.statusLabel} />
                {f.employment?.sugTochnit && (
                  <span style={{ fontSize: 10, color: "#64748b", background: "#f1f5f9", borderRadius: 6, padding: "1px 6px" }}>
                    {f.employment.sugTochnit}
                  </span>
                )}
              </div>
              {/* Row 3: financials */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 12 }}>
                {c.sumTagmulim > 0 && (
                  <span><span style={{ color: "#94a3b8", fontSize: 10 }}>תגמולים </span><b>{fmtMoney(c.sumTagmulim)}</b></span>
                )}
                {c.sumPitzuyim > 0 && (
                  <span><span style={{ color: "#94a3b8", fontSize: 10 }}>פיצויים </span><b>{fmtMoney(c.sumPitzuyim)}</b></span>
                )}
                {c.dmeiTzvPct && (
                  <span><span style={{ color: "#94a3b8", fontSize: 10 }}>ד.ניהול </span>{fmtPct(c.dmeiTzvPct)}</span>
                )}
                {c.tsua != null && (
                  <span style={{ color: "#16a34a" }}><span style={{ color: "#94a3b8", fontSize: 10 }}>תשואה </span>{fmtPct(c.tsua)}</span>
                )}
                {c.growth !== null && (
                  <span style={{ fontWeight: 600, color: c.growth >= 0 ? "#16a34a" : "#dc2626" }}>
                    {c.growth >= 0 ? "▲" : "▼"}{Math.abs(c.growth)}% YTD
                  </span>
                )}
              </div>
              {/* Row 4: balance + expand button */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: cs.accent }}>
                  {c.redeem > 0 ? fmtMoney(c.redeem) : ""}
                </span>
                
                <div style={{ display: "flex", gap: 6, marginRight: "auto", marginLeft: 12 }}>
                  {/* Notes button */}
                  {(() => {
                    const key = getFundUniqueKey(clientId, f);
                    const val = key ? notes[key] : "";
                    return (
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          const newVal = window.prompt("הזן הערה (השאר ריק למחיקה):", val || "");
                          if (newVal !== null && key) setNote(key, newVal);
                        }}
                        style={{
                          fontSize: 12, color: val ? "#1e293b" : "#94a3b8",
                          background: val ? "#fef08a" : "transparent",
                          border: val ? "1px solid #facc15" : "1px solid #e2e8f0",
                          padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                          fontWeight: val ? 700 : 600,
                        }}
                      >
                        {val ? "📝 הערה" : "➕ הערה"}
                      </span>
                    );
                  })()}

                  {/* Recommendation button */}
                  {(() => {
                    const recVal = snapshot?.fundComments?.[f.id] || "";
                    return (
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          const newVal = window.prompt("הזן המלצת יועץ (השאר ריק למחיקה):", recVal || "");
                          if (newVal !== null) updateFundComment(f.id, newVal);
                        }}
                        style={{
                          fontSize: 12, color: recVal ? "#1e293b" : "#94a3b8",
                          background: recVal ? "#bbf7d0" : "transparent",
                          border: recVal ? "1px solid #4ade80" : "1px solid #e2e8f0",
                          padding: "4px 10px", borderRadius: 8, cursor: "pointer",
                          fontWeight: recVal ? 700 : 600,
                        }}
                      >
                        {recVal ? "💬 המלצה" : "➕ המלצה"}
                      </span>
                    );
                  })()}
                </div>

                <span style={{
                  fontSize: 11, fontWeight: 700, color: cs.accent,
                  background: `${cs.accent}14`, border: `1px solid ${cs.accent}55`,
                  borderRadius: 6, padding: "3px 10px",
                }}>
                  {isOpen ? "סגור ▲" : "פירוט ▸"}
                </span>
              </div>
            </div>
            {/* Expanded tabs */}
            {isOpen && <ExpandedPanel fund={f} computed={c} />}
          </div>
        );
      })}
    </div>
  );
}
